import { useState, useRef, useCallback, useMemo } from 'react';
import { useLatestRef } from '@federa/components/hooks/useLatestRef';
import { generateUuid } from '@federa/utils/crypto/uuid';
import { errorMessages } from '@federa/utils/styles/content-tokens';
import { useEnterKeyHandler } from '@federa/components/hooks/useEnterKeyHandler';
import { secureLogger } from '@federa/utils/deps/registry';
import { LOG_EVENTS } from '@federa/const/logging';
import { isAuthorizedForMissionControl } from '@federa/utils/auth/role-normalization-core';
import { useAppDispatch, useMessagingState, useUserState } from './state.tsx';
import { useAssistant } from './AssistantProvider.tsx';
import {
	useComputedValues,
	useDataFetching,
	useConnectionMonitor,
	useMessageScrolling,
	uploadFiles,
} from './AssistantPane.hooks.effects.ts';
import type { ChatRoomStruct } from '@federa/types/conversations-chat-structs';
import type {
	AssistantCoreState as CoreState,
	DataFetchingConfig,
	FileUploadMetadata,
	MessageHandlers,
	SelectedFile,
	HandlerConfig,
} from '@federa/types/ui-chat-messaging';

function useCoreState(): CoreState {
	const [input, setInput] = useState('');
	const [showConversationList, setShowConversationList] = useState(false);
	const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
	const [wsConnected, setWsConnected] = useState(true);
	const [everConnected, setEverConnected] = useState(false);
	const [sendError, setSendError] = useState<string | null>(null);

	const messagesEndRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLTextAreaElement>(null);
	const hasDisplayedMessagesRef = useRef(false);
	const previousMessageCountRef = useRef(0);
	const inputValueRef = useLatestRef(input);

	return {
		input,
		setInput,
		showConversationList,
		setShowConversationList,
		selectedFiles,
		setSelectedFiles,
		wsConnected,
		setWsConnected,
		everConnected,
		setEverConnected,
		sendError,
		setSendError,
		messagesEndRef,
		inputRef,
		hasDisplayedMessagesRef,
		previousMessageCountRef,
		inputValueRef,
	};
}

function useFileHandlers(config: HandlerConfig) {
	const { setSelectedFiles } = config;
	const handleFilesSelected = useCallback(
		(files: File[]) => {
			const newFiles = files.map((file) => ({
				file,
				id: `file-${generateUuid()}`,
				uploading: false,
				progress: 0,
			}));
			setSelectedFiles((prev) => [...prev, ...newFiles]);
		},
		[setSelectedFiles],
	);
	const handleRemoveFile = useCallback(
		(fileId: string) => {
			setSelectedFiles((prev) => prev.filter((f) => f.id !== fileId));
		},
		[setSelectedFiles],
	);
	return { handleFilesSelected, handleRemoveFile };
}

async function executeSendMessage(config: HandlerConfig): Promise<void> {
	const {
		inputValueRef,
		isUploading,
		selectedFiles,
		currentConversation,
		isConnected,
		sendMessage,
		userId,
		setSelectedFiles,
		setSendError,
		setInput,
	} = config;
	const currentInput = inputValueRef.current;
	if (isUploading || (!currentInput.trim() && selectedFiles.length === 0) || !currentConversation) return;
	if (!isConnected()) {
		setSendError('Connection lost. Reconnecting...');
		return;
	}
	setSendError(null);
	let filesMetadata: FileUploadMetadata[] = [];
	if (selectedFiles.length > 0) {
		try {
			filesMetadata = await uploadFiles({
				selectedFiles,
				setSelectedFiles,
				userId,
				conversationId: currentConversation.id,
			});
		} catch (error) {
			secureLogger.error(LOG_EVENTS.ASSISTANT_PANE_FILE_UPLOAD_FAILED, { error });
			setSendError(errorMessages.uploadFailed);
			setSelectedFiles((prev) => prev.map((file) => ({ ...file, uploading: false, progress: 0 })));
			return;
		}
	}
	const sent = sendMessage(currentInput.trim() || '[Attachment]', filesMetadata.length > 0 ? filesMetadata : undefined);
	if (!sent) {
		setSendError(errorMessages.generic);
		return;
	}
	setInput('');
	setSelectedFiles([]);
}

function useSendHandler(config: HandlerConfig) {
	return useCallback(async () => {
		await executeSendMessage(config);
	}, [config]);
}

function useInputHandlers(config: HandlerConfig, handleSendMessage: () => Promise<void>) {
	const { setInput } = config;
	const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => setInput(e.target.value), [setInput]);
	const handleKeyDown = useEnterKeyHandler(handleSendMessage);
	return { handleInputChange, handleKeyDown };
}

function useConversationHandler(config: HandlerConfig) {
	const { switchConversation, setShowConversationList, hasDisplayedMessagesRef, previousMessageCountRef, inputRef } = config;
	return useCallback(
		(room: ChatRoomStruct) => {
			switchConversation(room);
			setShowConversationList(false);
			hasDisplayedMessagesRef.current = false;
			previousMessageCountRef.current = 0;
			inputRef.current?.focus();
		},
		[switchConversation, setShowConversationList, hasDisplayedMessagesRef, previousMessageCountRef, inputRef],
	);
}

function useMessageHandlers(config: HandlerConfig): MessageHandlers {
	const fileHandlers = useFileHandlers(config);
	const handleSendMessage = useSendHandler(config);
	const inputHandlers = useInputHandlers(config, handleSendMessage);
	const handleSelectConversation = useConversationHandler(config);
	return {
		...fileHandlers,
		handleSendMessage,
		...inputHandlers,
		handleSelectConversation,
	};
}

function useAssistantPaneCore() {
	const dispatch = useAppDispatch();
	const user = useUserState();
	const messaging = useMessagingState();
	const assistant = useAssistant();
	const state = useCoreState();
	const isG4 = isAuthorizedForMissionControl(user.role);
	const computed = useComputedValues({
		currentConversation: assistant.currentConversation,
		currentMessages: messaging.currentMessages,
		selectedFiles: state.selectedFiles,
		input: state.input,
		wsConnected: state.wsConnected,
		chatRoomList: messaging.chatRoomList,
	});
	return { dispatch, user, messaging, assistant, state, isG4, computed };
}

function useAssistantPaneEffects(core: ReturnType<typeof useAssistantPaneCore>) {
	const { dispatch, user, assistant, state, isG4, computed } = core;
	const dataConfig: DataFetchingConfig = {
		userId: user.userId,
		role: user.role,
		canSwitchConversations: isG4,
		currentConversation: assistant.currentConversation,
		currentMessages: core.messaging.currentMessages,
		dispatch,
		messagesEndRef: state.messagesEndRef,
	};
	const historyPaging = useDataFetching(dataConfig);
	useConnectionMonitor(assistant.isConnected, {
		sendError: state.sendError,
		setWsConnected: state.setWsConnected,
		setEverConnected: state.setEverConnected,
		setSendError: state.setSendError,
	});
	useMessageScrolling(computed.displayMessages, {
		messagesEndRef: state.messagesEndRef,
		hasDisplayedMessagesRef: state.hasDisplayedMessagesRef,
		previousMessageCountRef: state.previousMessageCountRef,
		agentStatusActive: Boolean(core.messaging.agentStatus),
	});
	return historyPaging;
}

export function useAssistantPane() {
	const core = useAssistantPaneCore();
	const historyPaging = useAssistantPaneEffects(core);
	const { user, messaging, assistant, state, isG4, computed } = core;
	// Memoize the handler config so its identity is stable across renders when no constituent field changed; a fresh object literal each render would defeat the downstream useCallback memoization (which keys on `config`) and cascade new handler identities into useEnterKeyHandler and the textarea/send-button props.
	const handlerConfig: HandlerConfig = useMemo(
		() => ({
			inputValueRef: state.inputValueRef,
			isUploading: computed.isUploading,
			selectedFiles: state.selectedFiles,
			currentConversation: assistant.currentConversation,
			isConnected: assistant.isConnected,
			sendMessage: assistant.sendMessage,
			userId: user.userId,
			setSelectedFiles: state.setSelectedFiles,
			setSendError: state.setSendError,
			setInput: state.setInput,
			switchConversation: assistant.switchConversation,
			setShowConversationList: state.setShowConversationList,
			hasDisplayedMessagesRef: state.hasDisplayedMessagesRef,
			previousMessageCountRef: state.previousMessageCountRef,
			inputRef: state.inputRef,
		}),
		[
			state.inputValueRef,
			computed.isUploading,
			state.selectedFiles,
			assistant.currentConversation,
			assistant.isConnected,
			assistant.sendMessage,
			user.userId,
			state.setSelectedFiles,
			state.setSendError,
			state.setInput,
			assistant.switchConversation,
			state.setShowConversationList,
			state.hasDisplayedMessagesRef,
			state.previousMessageCountRef,
			state.inputRef,
		],
	);
	const handlers = useMessageHandlers(handlerConfig);
	return {
		...state,
		initializing: assistant.isInitializing,
		isG4,
		canSwitchConversations: isG4,
		displayMessages: computed.displayMessages,
		uploadProgress: computed.uploadProgress,
		isUploading: computed.isUploading,
		canSend: computed.canSend,
		sortedConversations: computed.sortedConversations,
		user,
		messaging,
		currentConversation: assistant.currentConversation,
		sendMessage: assistant.sendMessage,
		isConnected: assistant.isConnected,
		switchConversation: assistant.switchConversation,
		paneRef: assistant.paneRef,
		...historyPaging,
		...handlers,
	};
}
